// What happens when a citizen reopens a PQRSD after its final answer, as the
// institution configures it in Config → PQRSD → General (config/pqrsd):
//
//   reopenTermMode — the new response deadline:
//     "reset"    full legal term for the type again, from the reopening (default)
//     "continue" the business days that were left when the final answer went
//                out: the clock was paused while the citizen reviewed it
//     "original" the deadline does not move (it may already be past: overdue)
//     "fixed"    reopenFixedDays business days from the reopening
//   maxReopens   — how many times a citizen may reopen; 0 = no limit.
//
// Ley 1755 does not regulate reopening an answered petition, so this is an
// institutional policy. One file serves handlePqrsdPublic (which applies it)
// and the admin screen (which edits it). Plain CommonJS for both.

const REOPEN_TERM_MODES = ["reset", "continue", "original", "fixed"];
const DEFAULT_REOPEN_POLICY = { reopenTermMode: "reset", reopenFixedDays: 5, maxReopens: 5 };
const MAX_FIXED_DAYS = 90;
const MAX_REOPENS_CAP = 50;

const REOPEN_TERM_LABELS = {
  reset: "Reiniciar el plazo completo",
  continue: "Continuar el plazo que quedaba",
  original: "Mantener la fecha límite original",
  fixed: "Días hábiles fijos",
};

const REOPEN_TERM_HELP = {
  reset: "La PQRSD recibe de nuevo el término completo de su tipo (p. ej. 15 días hábiles), contado desde la reapertura.",
  continue: "El reloj se detuvo mientras el ciudadano revisaba la respuesta: recibe los días hábiles que le quedaban cuando se envió. Si se respondió vencida, queda vencida.",
  original: "La fecha límite no cambia. Si ya pasó, la PQRSD queda vencida en cuanto se reabre.",
  fixed: "La PQRSD recibe un número fijo de días hábiles desde la reapertura, sin importar su tipo.",
};

const intIn = (v, min, max, fallback) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n >= min && n <= max ? n : fallback;
};

// Whatever is stored, as a usable policy.
function normalizeReopenPolicy(cfg) {
  const c = cfg && typeof cfg === "object" ? cfg : {};
  return {
    reopenTermMode: REOPEN_TERM_MODES.includes(c.reopenTermMode) ? c.reopenTermMode : DEFAULT_REOPEN_POLICY.reopenTermMode,
    reopenFixedDays: intIn(c.reopenFixedDays, 1, MAX_FIXED_DAYS, DEFAULT_REOPEN_POLICY.reopenFixedDays),
    maxReopens: intIn(c.maxReopens, 0, MAX_REOPENS_CAP, DEFAULT_REOPEN_POLICY.maxReopens),
  };
}

// The admin form's problems (Spanish), or [].
function validateReopenPolicy(draft) {
  const errors = [];
  const d = draft || {};
  if (!REOPEN_TERM_MODES.includes(d.reopenTermMode)) errors.push("Elija qué pasa con el plazo al reabrir.");
  if (d.reopenTermMode === "fixed") {
    const n = Number(d.reopenFixedDays);
    if (!Number.isInteger(n) || n < 1 || n > MAX_FIXED_DAYS) {
      errors.push(`Los días fijos deben ser un número entero entre 1 y ${MAX_FIXED_DAYS}.`);
    }
  }
  const m = Number(d.maxReopens);
  if (!Number.isInteger(m) || m < 0 || m > MAX_REOPENS_CAP) {
    errors.push(`El límite de reaperturas debe ser un número entero entre 0 (sin límite) y ${MAX_REOPENS_CAP}.`);
  }
  return errors;
}

const canReopenAgain = (policy, reopenCount) =>
  policy.maxReopens === 0 || (Number(reopenCount) || 0) < policy.maxReopens;

const toDate = (v) => {
  if (!v) return null;
  const d = v.toDate ? v.toDate() : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * The deadline after a reopening.
 * @param {object} policy - normalizeReopenPolicy output.
 * @param {object} p - the stored pqrsd (slaDeadline, respondedAt, responses).
 * @param {object} ctx - { now, slaDays, addBusinessDays, diffBusinessDays }.
 * @returns {{ deadline: Date, moved: boolean, mode: string, label: string, note: string|null }}
 */
function reopenDeadline(policy, p, { now, slaDays, addBusinessDays, diffBusinessDays }) {
  const original = toDate(p.slaDeadline);
  const label = (mode) => REOPEN_TERM_LABELS[mode];
  switch (policy.reopenTermMode) {
    case "fixed":
      return { deadline: addBusinessDays(now, policy.reopenFixedDays), moved: true, mode: "fixed", label: `${label("fixed")}: ${policy.reopenFixedDays}`, note: null };
    case "original":
      if (original) return { deadline: original, moved: false, mode: "original", label: label("original"), note: null };
      break; // nothing to keep: fall back below
    case "continue": {
      const lastResponse = Array.isArray(p.responses) && p.responses.length ? p.responses[p.responses.length - 1] : null;
      const answeredAt = toDate(p.respondedAt) || toDate(lastResponse && lastResponse.filedAt);
      if (original && answeredAt) {
        const left = Math.max(0, diffBusinessDays(answeredAt, original));
        return {
          deadline: left > 0 ? addBusinessDays(now, left) : now,
          moved: true,
          mode: "continue",
          label: `${label("continue")}: ${left}`,
          note: left > 0 ? null : "Se respondió sin días restantes: queda vencida.",
        };
      }
      break; // no answer date or no deadline: fall back below
    }
    default:
      break;
  }
  return {
    deadline: addBusinessDays(now, slaDays),
    moved: true,
    mode: "reset",
    label: label("reset"),
    note: policy.reopenTermMode === "reset" ? null : `Sin datos para "${label(policy.reopenTermMode)}": se reinició el plazo.`,
  };
}

module.exports = {
  REOPEN_TERM_MODES,
  REOPEN_TERM_LABELS,
  REOPEN_TERM_HELP,
  DEFAULT_REOPEN_POLICY,
  MAX_FIXED_DAYS,
  MAX_REOPENS_CAP,
  normalizeReopenPolicy,
  validateReopenPolicy,
  canReopenAgain,
  reopenDeadline,
};
